import { utf8Length } from './epc-qr.js';
import { SEPA_REMITTANCE_MAX, sepaText } from './sepa.js';

/**
 * Vorlage für den Verwendungszweck der Auszahlung je Basar (Einstellungs-Audit, Punkt 19). Gilt für GiroCode und
 * SEPA-Sammeldatei. Platzhalter: `{basar}` (Name des Basars) und `{nummer}` (Verkäufernummer). Das Ergebnis liegt immer im
 * SEPA-Zeichensatz und hat höchstens 140 Zeichen (`pain.001.001.09`, `Ustrd`). Reine Funktionen.
 */

export const PAYOUT_REMITTANCE_DEFAULT = 'Basar {basar} Nr. {nummer} Auszahlung';
/** Länge der Vorlage (mit Platzhaltern); der Rest bis 140 Zeichen bleibt für Basarname und Nummer. */
export const PAYOUT_REMITTANCE_TEMPLATE_MAX = 100;
export const PAYOUT_REMITTANCE_PLACEHOLDERS = ['{basar}', '{nummer}'] as const;

const PLACEHOLDER = /\{[^{}]*\}/g;

/** Stellen der Verkäufernummer, mit denen die Prüfung rechnet (Nummern bis 99999). */
const NUMBER_DIGITS = 5;
/** Höchste Länge des festen Teils nach der SEPA-Umschreibung, damit für den Basarnamen Platz bleibt. */
const FIXED_PART_MAX = 120;
const SEPA_BASIC = /^[A-Za-z0-9/\-?:().,'+ ]$/;

/** Zeichen der Vorlage: SEPA-Grundzeichen, Umlaute und Buchstaben mit Akzent; keine Kompatibilitätszeichen (ﬁ, Vollbreite). */
function templateCharAllowed(char: string): boolean {
  if (SEPA_BASIC.test(char)) return true;
  return (
    /^\p{L}$/u.test(char) &&
    char.normalize('NFKD') === char.normalize('NFD') &&
    sepaText(char, 10) !== ''
  );
}

/** Prüft die Vorlage und liefert die Fehlermeldung (Deutsch) oder `null`, wenn sie gültig ist. */
export function payoutRemittanceTemplateError(template: string): string | null {
  if (template.trim() === '') return 'Bitte einen Text eintragen.';
  if (template.length > PAYOUT_REMITTANCE_TEMPLATE_MAX) {
    return `Höchstens ${String(PAYOUT_REMITTANCE_TEMPLATE_MAX)} Zeichen.`;
  }
  const found: string[] = template.match(PLACEHOLDER) ?? [];
  if (
    found.some((entry) => !(PAYOUT_REMITTANCE_PLACEHOLDERS as readonly string[]).includes(entry))
  ) {
    return 'Erlaubt sind nur die Platzhalter {basar} und {nummer}.';
  }
  if (!found.includes('{nummer}')) {
    return 'Der Platzhalter {nummer} ist nötig, damit die Zahlung zuordenbar bleibt.';
  }
  const rest = template.replace(PLACEHOLDER, '');
  if (/[{}]/.test(rest)) return 'Geschweifte Klammern sind nur für Platzhalter erlaubt.';
  if (/[^\S ]/u.test(rest)) return 'Nur normale Leerzeichen sind erlaubt.';
  let fixed = 0;
  for (const char of rest) {
    if (!templateCharAllowed(char)) {
      return 'Nur Buchstaben, Ziffern und gängige Satzzeichen sind erlaubt (SEPA-Zeichensatz).';
    }
    fixed += char === ' ' ? 1 : sepaText(char, 10).length;
  }
  fixed += found.filter((entry) => entry === '{nummer}').length * NUMBER_DIGITS;
  if (fixed > FIXED_PART_MAX) {
    return 'Der Text ist nach dem Umschreiben der Umlaute zu lang (Platz für den Basarnamen fehlt).';
  }
  return null;
}

/**
 * Setzt Basarname und Verkäufernummer ein. Ist das Ergebnis zu lang, kürzt es den Basarnamen (nie die Nummer, damit die
 * Zahlung zuordenbar bleibt). Mit `sepa` wird alles in den SEPA-Zeichensatz umgeschrieben (Sammelüberweisung); ohne bleibt der
 * Name des Basars wie er ist (GiroCode, EPC erlaubt UTF-8), wie bisher. `maxBytes` begrenzt zusätzlich die Länge in UTF-8-Byte
 * (Budget des GiroCodes). Passt selbst der Text ohne Basarnamen nicht, gilt der Standardtext.
 */
export function renderPayoutRemittance(
  template: string,
  title: string,
  sellerNumber: number,
  options: { sepa?: boolean; maxBytes?: number } = {},
): string {
  const sepa = options.sepa !== false;
  const maxBytes = options.maxBytes ?? Number.POSITIVE_INFINITY;
  const clean = (text: string, max: number): string =>
    sepa ? sepaText(text, max) : Array.from(text.trim()).slice(0, max).join('');
  const attempt = (source: string): string | null => {
    const withNumber = source.replaceAll('{nummer}', String(sellerNumber));
    const full = Array.from(clean(title, SEPA_REMITTANCE_MAX));
    const build = (room: number): string => {
      const text = withNumber.replaceAll('{basar}', full.slice(0, room).join(''));
      return sepa ? sepaText(text, 1000) : text.trim();
    };
    const fits = (out: string): boolean =>
      Array.from(out).length <= SEPA_REMITTANCE_MAX && utf8Length(out) <= maxBytes;
    // Der Name wird so lange gekürzt, bis alles passt (Umschreibung in den SEPA-Zeichensatz ändert Längen)
    for (let room = full.length; room >= 0; room -= 1) {
      const out = build(room);
      if (fits(out)) return out;
    }
    return null;
  };
  const usable =
    payoutRemittanceTemplateError(template) === null ? template : PAYOUT_REMITTANCE_DEFAULT;
  return (
    attempt(usable) ?? attempt(PAYOUT_REMITTANCE_DEFAULT) ?? sepaText(String(sellerNumber), 140)
  );
}
